import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { type Column, DataTable } from "@/components/admin/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAdminCourses, useArchiveCourse } from "@/hooks/useAdminData";
import type { Course } from "@/lib/api-types";
import { MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

export function AdminCourseList() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading } = useAdminCourses();
	const archiveMutation = useArchiveCourse();
	const [deleteTarget, setDeleteTarget] = useState<Course | null>(null);

	const courses = data?.courses ?? [];

	const columns: Column<Course & Record<string, unknown>>[] = [
		{
			key: "title",
			header: t("admin.courseTitle"),
			sortable: true,
			render: (item) => <span className="font-medium text-primary">{item.title}</span>,
		},
		{
			key: "language",
			header: t("admin.language"),
			sortable: true,
			render: (item) => (
				<Badge variant="outline" className="uppercase">
					{item.language}
				</Badge>
			),
		},
		{
			key: "module_count",
			header: t("admin.modules"),
			sortable: true,
		},
		{
			key: "actions",
			header: "",
			render: (item) => (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="icon">
							<MoreHorizontal className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => navigate(`/admin/courses/${item.id}`)}>
							<Pencil className="mr-2 h-4 w-4" />
							{t("admin.edit")}
						</DropdownMenuItem>
						<DropdownMenuItem className="text-error" onClick={() => setDeleteTarget(item)}>
							<Trash2 className="mr-2 h-4 w-4" />
							{t("admin.delete")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			),
		},
	];

	function handleDelete() {
		if (!deleteTarget) return;
		archiveMutation.mutate(deleteTarget.id, {
			onSuccess: () => setDeleteTarget(null),
		});
	}

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[{ label: t("admin.dashboard"), href: "/admin" }, { label: t("admin.courses") }]}
			/>

			<div className="flex items-center justify-between">
				<h1 className="text-3xl font-bold text-primary">{t("admin.courses")}</h1>
				<Button asChild>
					<Link to="/admin/courses/new">
						<Plus className="mr-2 h-4 w-4" />
						{t("admin.newCourse")}
					</Link>
				</Button>
			</div>

			<DataTable
				columns={columns}
				data={courses as (Course & Record<string, unknown>)[]}
				isLoading={isLoading}
				emptyMessage={t("admin.noCourses")}
				searchPlaceholder={t("admin.searchCourses")}
				searchFilter={(item, query) =>
					(item.title as string).toLowerCase().includes(query.toLowerCase())
				}
			/>

			<Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("admin.deleteCourse")}</DialogTitle>
						<DialogDescription>
							{t("admin.deleteCourseConfirm", {
								title: deleteTarget?.title,
							})}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteTarget(null)}>
							{t("admin.cancel")}
						</Button>
						<Button
							variant="destructive"
							onClick={handleDelete}
							disabled={archiveMutation.isPending}
						>
							{t("admin.delete")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
